import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Wallet, BarChart3, Bell, Users, BookOpen, Megaphone, Shield, CreditCard, Trophy,
  ArrowLeftRight, Star, ChevronDown, MessagesSquare,
  Search, LogOut, TrendingUp, Loader2, Trash2, X, Menu, ShieldCheck,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/FilterContext";
import { useMoney } from "../context/MoneyContext";
import { CURRENCIES } from "./ui";
import Logo from "./Logo";
import AdminPreviewBar from "./AdminPreviewBar";
import BottomNav from "./BottomNav";
import LegalGate from "./LegalGate";
import { CookieSettingsLink } from "./CookieConsent";
import { api } from "../lib/api";
import { shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

/**
 * Navigation is grouped by the job the user is doing, not by feature.
 *
 * A flat list had grown to twelve entries, which is past the point where you scan
 * and into the point where you read. Three labelled groups — my money / other
 * people / my account — means three things to scan instead of twelve, and gives new
 * surfaces an obvious home instead of lengthening the list again.
 *
 * Groups are collapsible and remember their state, so someone who never touches
 * Community can fold it away permanently.
 *
 * An item with a `feature` is only listed while that product area is open to this
 * account (backend/features.py), and a group left with no items is not shown at all.
 */
const NAV_GROUPS = [
  {
    key: "portfolio",
    items: [
      { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
      { to: "/holdings", key: "holdings", icon: Wallet },
      { to: "/transactions", key: "transactions", icon: ArrowLeftRight },
      { to: "/watchlist", key: "watchlist", icon: Star },
      { to: "/analytics", key: "analytics", icon: BarChart3 },
      { to: "/insurance", key: "insurance", icon: ShieldCheck },
    ],
  },
  {
    key: "community",
    items: [
      { to: "/leaderboard", key: "leaderboard", icon: Trophy, feature: "community" },
      { to: "/investors", key: "investors", icon: Users, feature: "community" },
      // Shown to everyone the area is open to: an investor needs it to answer requests,
      // and a member on a lesser plan should see what the conversation is before buying it.
      { to: "/messages", key: "messages", icon: MessagesSquare, feature: "messages" },
    ],
  },
  {
    key: "account",
    items: [
      { to: "/alerts", key: "alerts", icon: Bell },
      { to: "/billing", key: "billing", icon: CreditCard, feature: "payments" },
      { to: "/help", key: "help", icon: BookOpen },
    ],
  },
];

// Appended only for the roles that can use them, so a normal user never sees a link
// that would bounce them straight back to the dashboard.
const ROLE_GROUPS = {
  investor: [{ key: "publishing", items: [
    { to: "/studio", key: "studio", icon: Megaphone },
    { to: "/partner", key: "partner", icon: Wallet, feature: "payments" },
  ]}],
  admin: [{ key: "publishing", items: [
    { to: "/studio", key: "studio", icon: Megaphone },
    { to: "/partner", key: "partner", icon: Wallet, feature: "payments" },
    { to: "/admin", key: "admin", icon: Shield },
  ]}],
};

function NavGroup({ group, onNavigate, t }) {
  const storageKey = `costock_nav_${group.key}`;
  const [open, setOpen] = useState(() => localStorage.getItem(storageKey) !== "0");
  const toggle = () => {
    setOpen((o) => { localStorage.setItem(storageKey, o ? "0" : "1"); return !o; });
  };
  return (
    <div className="mb-1">
      <button onClick={toggle} data-testid={`navgroup-${group.key}`}
              className="w-full flex items-center justify-between px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 hover:text-slate-600 transition-colors">
        {t(`navGroups.${group.key}`)}
        <ChevronDown size={13} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 mt-0.5">
          {group.items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-brand-50 text-brand-600" : "text-slate-600 hover:bg-slate-100"}`}>
              <n.icon size={17} strokeWidth={1.7} />
              {t(`nav.${n.key}`)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function GlobalSearch() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.search(q);
        setResults(res.data);
        setOpen(true);
      } catch (e) {} finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const go = (ticker) => {
    setOpen(false); setQ("");
    navigate(`/instrument/${ticker}`);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder={t("common.searchPlaceholder")}
          className="input pl-11 pr-10 bg-slate-50/70"
        />
        {loading && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" size={16} />}
      </div>
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18 }}
            data-testid="search-results"
            className="absolute mt-2 w-full bg-white rounded-2xl border border-slate-200/70 shadow-soft overflow-hidden z-50"
          >
            {results.map((r) => (
              <button
                key={r.ticker}
                data-testid={`search-result-${r.ticker}`}
                onClick={() => go(r.ticker)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-50 transition-colors text-left"
              >
                <div>
                  <div className="font-medium text-slate-900">{r.ticker}</div>
                  <div className="text-xs text-slate-500 truncate max-w-[240px]">{r.name}</div>
                </div>
                <span className="pill bg-slate-100 text-slate-500">{r.asset_type}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NotificationsBell() {
  const { t } = useTranslation();
  const { items, unread, reload } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && unread > 0) { await api.markNotificationsRead(); reload(); }
  };
  const remove = async (id) => { await api.deleteNotification(id); reload(); };
  return (
    <div ref={ref} className="relative">
      <button data-testid="notifications-btn" onClick={toggle} className="relative p-2.5 rounded-xl hover:bg-slate-100 transition-colors">
        <Bell size={19} className="text-slate-600" strokeWidth={1.6} />
        {unread > 0 && (
          <span data-testid="notifications-badge" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand-500 text-white text-[10px] flex items-center justify-center">{unread}</span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18 }}
            data-testid="notifications-panel"
            className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-slate-200/70 shadow-soft z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-slate-100 font-heading font-semibold text-sm">{t("notifications.title")}</div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">{t("notifications.empty")}</div>}
              {/* Every notification is about something, so every notification opens it.
                  The rows used to be inert text: "new comment on your post" and then it
                  was on you to go and find it. The server now sends a `link` with each
                  one (notify.py), including an anchor to the exact comment. Rows
                  without a link stay as plain text rather than pretending to be
                  clickable — an old row written before this will simply not navigate. */}
              {items.map((n) => {
                const Row = n.link ? NavLink : "div";
                const rowProps = n.link
                  ? { to: n.link, onClick: () => setOpen(false) }
                  : {};
                return (
                  <div key={n.id} className="border-b border-slate-50 flex items-start gap-2 hover:bg-slate-50 pr-2">
                    <Row {...rowProps}
                         data-testid={`notification-${n.id}`}
                         className={`flex-1 flex items-start gap-2 px-4 py-3 min-w-0 ${n.link ? "cursor-pointer" : ""}`}>
                      <TrendingUp size={16} className="text-brand-500 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className={`text-sm ${n.read ? "text-slate-500" : "text-slate-800 font-medium"}`}>
                          {n.message}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{shortDate(n.created_at)}</div>
                      </div>
                    </Row>
                    <button onClick={() => remove(n.id)}
                            aria-label={t("common.delete")}
                            className="text-slate-300 hover:text-rose-500 self-center shrink-0">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SidebarInner({ onNavigate }) {
  const { t } = useTranslation();
  const { user, logout, isRealAdmin, adminPreview, features } = useAuth();
  const { homeCurrency, setHomeCurrency, language, setLanguage, numberLocale, setNumberLocale } = useMoney();
  return (
    <>
      <div className="px-2"><Logo size={34} /></div>
      <nav className="mt-8 flex flex-col">
        {[...NAV_GROUPS, ...(ROLE_GROUPS[user?.role] || [])]
          .map((g) => ({ ...g, items: g.items.filter((n) => !n.feature || features[n.feature]) }))
          .filter((g) => g.items.length > 0)
          .map((g) => (
            <NavGroup key={g.key} group={g} onNavigate={onNavigate} t={t} />
          ))}
      </nav>
      <div className="mt-4"><AdminPreviewBar /></div>

      {/* An admin is never sold a subscription — the server already grants them paid
          access, so the prompt would be an upsell for something they have. While
          previewing, though, the upsell is exactly what we want to see: the whole
          point is to look at the product the way a free member looks at it. */}
      {features.payments && user?.tier !== "paid" && (!isRealAdmin || adminPreview) && (
        <NavLink to="/billing" onClick={onNavigate} data-testid="sidebar-upgrade"
          className="mt-4 rounded-xl bg-brand-50 border border-brand-100 p-3.5 block hover:bg-brand-100/60 transition-colors">
          <div className="flex items-center gap-2 text-brand-700 font-medium text-sm">
            <CreditCard size={15} /> {t("billing.upgrade")}
          </div>
          <p className="text-xs text-slate-500 mt-1 leading-snug">{t("billing.sidebarPitch")}</p>
        </NavLink>
      )}

      <div className="mt-auto pt-6 border-t border-slate-100 space-y-3">
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-1 text-[11px] text-slate-400">
          <NavLink to="/identity" onClick={onNavigate} className="hover:text-brand-600">{t("nav.identity")}</NavLink>
          <NavLink to="/security" onClick={onNavigate} className="hover:text-brand-600">{t("nav.security")}</NavLink>
          <NavLink to="/imprint" onClick={onNavigate} className="hover:text-brand-600">{t("legal.imprint")}</NavLink>
          <NavLink to="/privacy" onClick={onNavigate} className="hover:text-brand-600">{t("legal.privacy")}</NavLink>
          <NavLink to="/terms" onClick={onNavigate} className="hover:text-brand-600">{t("legal.terms")}</NavLink>
          <CookieSettingsLink className="hover:text-brand-600" />
        </div>
        <button data-testid="logout-btn" onClick={logout} className="w-full btn-ghost justify-start text-slate-500 text-sm">
          <LogOut size={16} /> {t("common.signOut")}
        </button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white flex">
      {/* Blocks the product — not the site — until the current terms and privacy
          policy have been accepted. Legal pages and sign-out stay reachable. */}
      <LegalGate />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-slate-200/70 px-5 py-6 sticky top-0 h-screen overflow-y-auto overscroll-contain nav-scroll">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-white flex flex-col shadow-2xl"
            >
              {/* The close button sits outside the scroll area so it stays reachable
                  however far down the menu you are. */}
              <div className="flex justify-end px-4 pt-4 shrink-0">
                <button
                  data-testid="mobile-drawer-close"
                  onClick={() => setMenuOpen(false)}
                  className="p-1.5 -m-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>
              {/* min-h-0 is what actually lets a flex child scroll — without it the
                  nav simply grew past the bottom of the screen and the last items
                  (settings, legal, sign out) were unreachable. */}
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain nav-scroll px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                <SidebarInner onNavigate={() => setMenuOpen(false)} />
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass sticky top-0 z-40 px-4 sm:px-6 lg:px-10 py-4 flex items-center gap-3 sm:gap-4">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden text-slate-600 hover:text-brand-600 shrink-0"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <div className="lg:hidden shrink-0"><Logo size={26} /></div>
          <div className="flex-1 flex justify-end lg:justify-start min-w-0">
            <GlobalSearch />
          </div>
          <NotificationsBell />
        </header>
        {/* pb-24 on mobile keeps the last row clear of the fixed bottom nav. */}
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 sm:py-8 pb-24 md:pb-8 max-w-[1500px] w-full mx-auto">
          {children}
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
